import { IconButton, Stack, TextField } from '@chronosphereio/chrono-ui';
import { Circle as CircleIcon } from 'mdi-material-ui';
import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';

interface ColorPickerProps {
  color: string;
  onChange?: (color: string) => void;
  /**
   * Preset color palette
   */
  palette?: string[];
}

export const ColorPicker = ({ color, onChange, palette }: ColorPickerProps) => {
  // value is the visible value for the controlled text input
  const [value, setValue] = useState(color);

  const handleColorChange = (color: string) => {
    setValue(color);
    onChange?.(color);
  };

  // we should update this if https://github.com/omgovich/react-colorful/issues/157 is resolved
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/([^0-9A-F]+)/gi, '').substring(0, 8);
    setValue(`#${inputValue}`); // always prefix input value with # to indicate hex format
    // only set color if input value is a valid hex color
    if (isValidHex(e.target.value)) {
      onChange?.(e.target.value);
    }
  };

  return (
    <Stack spacing={1} width={'200px'}>
      <HexColorPicker color={color} onChange={handleColorChange} />
      <Stack direction="row" flexWrap="wrap" justifyContent="space-evenly">
        {palette &&
          palette.map((color, i) => (
            <IconButton
              key={i}
              aria-label={`change color to ${color}`}
              sx={{ color }}
              onClick={() => handleColorChange(color)}
            >
              <CircleIcon />
            </IconButton>
          ))}
      </Stack>
      <TextField
        inputProps={{ 'aria-label': 'enter hex color' }}
        fullWidth
        value={value}
        onChange={handleInputChange}
      />
    </Stack>
  );
};

const isValidHex = (value: string, alpha?: boolean): boolean => {
  const matcher = /^#?([0-9A-F]{3,8})$/i;
  const match = matcher.exec(value);
  const length = match && match[1] ? match[1].length : 0;
  return (
    length === 3 || // '#rgb' format
    length === 6 || // '#rrggbb' format
    (!!alpha && length === 4) || // '#rgba' format
    (!!alpha && length === 8) // '#rrggbbaa' format
  );
};
